import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AreaSelect from '../components/AreaSelect';
import { driverApi, dashboardApi } from '../api/mockApi';
import Avatar from '../components/Avatar';

const VEHICLE_TYPES = ['Saloon Car', 'Wheelchair Minibus', 'Black Cab', '7 Seater MPV', '9 Seater MPV', 'W/C Taxi'];
const QUALIFICATIONS = ['DBS', 'First Aid', 'Safeguard', 'PATS'];
const LICENSE_OPTIONS = ['PCO', 'LOCAL AUTHORITY', 'TAXI BADGE', 'PSV'];
const ROUTE_STATUSES = ['ON ROUTE', 'OFF ROUTE'];

const emptyPa = () => ({
  fullName: '', mobileNumber: '', address: '', postCode: '', area: '',
  qualifications: [], licenses: '', attachments: [], routeStatus: 'OFF ROUTE',
});

const emptyForm = () => ({
  fullName: '', address: '', postCode: '', area: '', mobileNumber: '',
  vehicle: '', qualifications: [], licenses: '', paAvailability: 'Not Available',
  routeStatus: 'OFF ROUTE', attachments: [], pa: emptyPa(),
});

const emptyFilters = () => ({ name: '', area: '', vehicle: '', routeName: '', routeStatus: '' });

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [filters, setFilters] = useState(emptyFilters());
  const [nameInput, setNameInput] = useState('');
  const [options, setOptions] = useState({ areas: [], routeNames: [], vehicles: [], statuses: ROUTE_STATUSES });
  const requestId = useRef(0);
  const navigate = useNavigate();

  // Debounce the free-text name filter (350ms).
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => (prev.name === nameInput ? prev : { ...prev, name: nameInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [nameInput]);

  const loadDrivers = useCallback(async () => {
    const myId = ++requestId.current;
    setLoading(true);
    const res = await driverApi.getAll({
      name: filters.name,
      area: filters.area,
      vehicle: filters.vehicle,
      routeName: filters.routeName,
      routeStatus: filters.routeStatus,
      include: 'pa',
    });
    if (myId !== requestId.current) return;
    const rows = res && res.success && Array.isArray(res.data) ? res.data : [];
    setDrivers([...rows].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    setLoading(false);
    setFirstLoad(false);
  }, [filters]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  useEffect(() => {
    const loadOptions = async () => {
      if (!dashboardApi || typeof dashboardApi.getFilters !== 'function') return;
      const res = await dashboardApi.getFilters();
      if (res && res.success && res.data) {
        setOptions({
          areas: res.data.areas || [],
          routeNames: res.data.routeNames || [],
          vehicles: res.data.vehicles || [],
          statuses: res.data.statuses || ROUTE_STATUSES,
        });
      }
    };
    loadOptions();
  }, []);

  const vehicleOptions = [
    ...VEHICLE_TYPES,
    ...(options.vehicles || []).filter(v => v && !VEHICLE_TYPES.includes(v)),
  ];

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const clearFilters = () => {
    setNameInput('');
    setFilters(emptyFilters());
  };

  const resetForm = () => {
    setForm(emptyForm());
    setFormError('');
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');

    const { pa, ...rest } = form;
    const payload = { ...rest };

    if (form.paAvailability === 'Available') {
      const details = pa || emptyPa();
      const paName = String(details.fullName || '').trim();
      const paMobile = String(details.mobileNumber || '').trim();
      if (!paName || !paMobile) {
        setFormError('PA Full Name and PA Mobile Number are required when PA Availability is "Available".');
        return;
      }
      payload.pa = { ...details, fullName: paName, mobileNumber: paMobile };
    }

    setSaving(true);
    const res = await driverApi.create(payload);
    setSaving(false);
    if (!res || !res.success) {
      setFormError((res && res.error) || 'Could not add the driver. Please try again.');
      return;
    }
    setShowModal(false);
    resetForm();
    loadDrivers();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      await driverApi.delete(id);
      loadDrivers();
    }
  };

  const toggleQual = (q) => {
    setForm(prev => ({
      ...prev,
      qualifications: prev.qualifications.includes(q)
        ? prev.qualifications.filter(x => x !== q)
        : [...prev.qualifications, q],
    }));
  };

  const updatePa = (patch) => {
    setForm(prev => ({ ...prev, pa: { ...(prev.pa || emptyPa()), ...patch } }));
  };

  const togglePaQual = (q) => {
    setForm(prev => {
      const pa = prev.pa || emptyPa();
      const quals = pa.qualifications || [];
      return {
        ...prev,
        pa: {
          ...pa,
          qualifications: quals.includes(q) ? quals.filter(x => x !== q) : [...quals, q],
        },
      };
    });
  };

  const paLabel = (d) => {
    if (d.pa && d.pa.fullName) return `${d.paAvailability || 'Available'} — ${d.pa.fullName}`;
    return d.paAvailability || 'N/A';
  };

  if (firstLoad && loading) return <div className="loading">Loading drivers...</div>;

  const formPa = form.pa || emptyPa();

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Drivers ({drivers.length})</h1>
        <button className="btn btn-gold" onClick={openModal}>+ Add Driver</button>
      </div>

      <div className="filters-bar">
        <div className="filter-field">
          <label htmlFor="driver-filter-name">Name</label>
          <input
            id="driver-filter-name"
            value={nameInput}
            placeholder="Search by name"
            onChange={e => setNameInput(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label htmlFor="driver-filter-area">Area</label>
          <select
            id="driver-filter-area"
            value={filters.area}
            onChange={e => setFilters({ ...filters, area: e.target.value })}
          >
            <option value="">All areas</option>
            {(options.areas || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="driver-filter-vehicle">Vehicle</label>
          <select
            id="driver-filter-vehicle"
            value={filters.vehicle}
            onChange={e => setFilters({ ...filters, vehicle: e.target.value })}
          >
            <option value="">All vehicles</option>
            {vehicleOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="driver-filter-route">Route</label>
          <select
            id="driver-filter-route"
            value={filters.routeName}
            onChange={e => setFilters({ ...filters, routeName: e.target.value })}
          >
            <option value="">All routes</option>
            {(options.routeNames || []).map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="driver-filter-status">Route Status</label>
          <select
            id="driver-filter-status"
            value={filters.routeStatus}
            onChange={e => setFilters({ ...filters, routeStatus: e.target.value })}
          >
            <option value="">All statuses</option>
            {(options.statuses || ROUTE_STATUSES).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filters-actions">
          <button className="btn btn-outline btn-sm" onClick={clearFilters} disabled={activeFilterCount === 0 && !nameInput}>
            Clear filters
          </button>
          <span className="filter-summary">
            {loading
              ? 'Loading...'
              : `${drivers.length} driver${drivers.length === 1 ? '' : 's'}${activeFilterCount ? ` (${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied)` : ''}`}
          </span>
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Area</th>
            <th>Mobile</th>
            <th>Vehicle</th>
            <th>Qualifications</th>
            <th>PA Available</th>
            <th>Route</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {drivers.map(d => (
            <tr key={d.id}>
              <td style={{ cursor: 'pointer', color: '#DBAD17' }} onClick={() => navigate(`/drivers/${d.id}`)}>
                <div className="name-cell">
                  <Avatar name={d.fullName} photoUrl={d.photoUrl} size={34} />
                  <span>{d.fullName || 'N/A'}</span>
                </div>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{d.area || '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{d.mobileNumber}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{d.vehicle || 'N/A'}</td>
              <td>
                <div className="qual-badges" style={{ flexWrap: 'nowrap' }}>
                  {QUALIFICATIONS.map(q => (
                    <span key={q} className={`qual-badge ${d.qualifications?.includes(q) ? 'active' : 'inactive'}`} style={{ fontSize: 11, padding: '3px 8px' }}>
                      {q}
                    </span>
                  ))}
                </div>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{paLabel(d)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{d.routeName || '—'}</td>
              <td>
                <span className={`status-badge ${d.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                  <span className="dot"></span>
                  {d.routeStatus}
                </span>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => navigate(`/drivers/${d.id}`)}>View</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(d.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {!drivers.length && !loading && (
            <tr>
              <td colSpan={9}>
                <p className="empty-state">No drivers match these filters.</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Add New Driver</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Full Name *</label>
                <input value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Mobile Number *</label>
                <input value={form.mobileNumber} onChange={e => setForm({...form, mobileNumber: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Post Code</label>
                <input value={form.postCode} onChange={e => setForm({...form, postCode: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Area</label>
                <AreaSelect
                  value={form.area || ''}
                  onChange={v => setForm({ ...form, area: v })}
                  blankLabel="Detect from post code"
                />
              </div>
              <div className="form-group">
                <label>Vehicle Type</label>
                <select value={form.vehicle} onChange={e => setForm({...form, vehicle: e.target.value})}>
                  <option value="">Select vehicle</option>
                  {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Qualifications</label>
                <div className="checkbox-group">
                  {QUALIFICATIONS.map(q => (
                    <div key={q} className={`checkbox-item ${form.qualifications.includes(q) ? 'checked' : ''}`} onClick={() => toggleQual(q)}>
                      {form.qualifications.includes(q) ? '✓' : '○'} {q}
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>License</label>
                <select value={form.licenses} onChange={e => setForm({...form, licenses: e.target.value})}>
                  <option value="">Select license</option>
                  {LICENSE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>PA Availability</label>
                <select value={form.paAvailability} onChange={e => setForm({...form, paAvailability: e.target.value})}>
                  <option value="Available">Available</option>
                  <option value="Not Available">Not Available</option>
                </select>
              </div>
              <div className="form-group">
                <label>Route Status</label>
                <select value={form.routeStatus} onChange={e => setForm({...form, routeStatus: e.target.value})}>
                  {ROUTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {form.paAvailability === 'Available' && (
                <div className="form-section">
                  <h4>Passenger Assistant Details</h4>
                  <p className="hint">This PA belongs to this driver and will be linked to them automatically.</p>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input value={formPa.fullName} onChange={e => updatePa({ fullName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <input value={formPa.mobileNumber} onChange={e => updatePa({ mobileNumber: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input value={formPa.address} onChange={e => updatePa({ address: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Post Code</label>
                    <input value={formPa.postCode} onChange={e => updatePa({ postCode: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Area</label>
                    <AreaSelect
                  value={formPa.area}
                  onChange={v => updatePa({ area: v })}
                  blankLabel="Detect from post code"
                />
                  </div>
                  <div className="form-group">
                    <label>Qualifications</label>
                    <div className="checkbox-group">
                      {QUALIFICATIONS.map(q => (
                        <div key={q} className={`checkbox-item ${formPa.qualifications?.includes(q) ? 'checked' : ''}`} onClick={() => togglePaQual(q)}>
                          {formPa.qualifications?.includes(q) ? '✓' : '○'} {q}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>License</label>
                    <select value={formPa.licenses} onChange={e => updatePa({ licenses: e.target.value })}>
                      <option value="">Select license</option>
                      {LICENSE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Route Status</label>
                    <select value={formPa.routeStatus} onChange={e => updatePa({ routeStatus: e.target.value })}>
                      {ROUTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {formError && <p className="error-text" style={{ textAlign: 'left' }}>{formError}</p>}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriversPage;
